import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Lock, Loader2 } from 'lucide-react';
import PasswordStrength, { isPasswordStrong } from '@/components/auth/PasswordStrength';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Used both as a routine "Change Password" page (via /change-password) and
 * as a forced one-time reset gate when must_change_password=true.
 */
export default function ChangePassword({ forced = false, embedded = false, onSuccess }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [strong, setStrong] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    if (!current) return toast.error('Enter your current password');
    if (!isPasswordStrong(next)) return toast.error('New password does not meet the policy');
    if (next !== confirm) return toast.error('New password and confirmation do not match');
    if (next === current) return toast.error('New password must be different from current password');
    setBusy(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/change-password`,
        { current_password: current, new_password: next },
        { withCredentials: true }
      );
      toast.success('Password changed successfully');
      // Wait for the user object to refresh BEFORE navigating, so the dashboard
      // doesn't briefly mount with the stale must_change_password=true flag.
      if (onSuccess) {
        onSuccess();
      } else if (embedded) {
        setCurrent(''); setNext(''); setConfirm('');
        try { await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }); } catch { /* ignore */ }
      } else {
        try { await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }); } catch { /* ignore */ }
        navigate('/dashboard');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to change password';
      toast.error(typeof msg === 'string' ? msg : 'Failed to change password');
    } finally {
      setBusy(false);
    }
  };

  const inner = (
    <Card className="w-full max-w-md">
        <CardHeader className="space-y-2">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-teal-100 mx-auto">
            <Lock className="w-6 h-6 text-teal-600" />
          </div>
          <CardTitle className="text-center text-xl">
            {forced ? 'Update Your Password' : 'Change Password'}
          </CardTitle>
          <CardDescription className="text-center">
            {forced
              ? 'For security, please set a stronger password before continuing.'
              : 'Use the password policy below to choose a strong password.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!forced && user && (
            <div className="mb-5 flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="profile-info">
              <div className="w-11 h-11 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-base font-bold shrink-0">
                {(user.name || user.email || 'A').trim().charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-base font-semibold text-slate-900 truncate" data-testid="profile-name">{user.name || 'Account'}</p>
                <p className="text-sm text-slate-600 truncate" data-testid="profile-email">{user.email}</p>
                <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-teal-100 text-teal-700 capitalize" data-testid="profile-role">{user.role || 'agent'}</span>
              </div>
            </div>
          )}
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="current">Current Password</Label>
              <div className="relative">
                <Input
                  id="current"
                  type={showCurrent ? 'text' : 'password'}
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  required
                  autoComplete="current-password"
                  data-testid="current-password-input"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                  data-testid="toggle-current-visibility"
                >
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="next">New Password</Label>
              <div className="relative">
                <Input
                  id="next"
                  type={showNext ? 'text' : 'password'}
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  required
                  autoComplete="new-password"
                  data-testid="new-password-input"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNext((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                  data-testid="toggle-new-visibility"
                >
                  {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <PasswordStrength password={next} onValidityChange={setStrong} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirm">Confirm New Password</Label>
              <Input
                id="confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                autoComplete="new-password"
                data-testid="confirm-password-input"
              />
              {confirm && next !== confirm && (
                <p className="text-xs text-red-600">Passwords do not match</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={busy || !strong || next !== confirm || !current}
              className="w-full bg-teal-600 hover:bg-teal-700"
              data-testid="submit-password-change-btn"
            >
              {busy
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating…</>
                : 'Change Password'}
            </Button>
          </form>
        </CardContent>
    </Card>
  );

  if (embedded) {
    return <div className="w-full" data-testid="change-password-page">{inner}</div>;
  }
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="change-password-page">
      {inner}
    </div>
  );
}
